import { TournamentState } from '@ps/poker-games-tile-mappers';
import type { Row } from 'react-table';

import type { FilterVariables, Tournament } from '@swf/poker';
import {
  criteriaMatches,
  filterMTTTournaments,
  parseHSPCriteria,
  stateTimeComparator
} from '@swf/poker';

import type { PPDataType, Pass, PassBundleRaw, StepKey } from '../types';
import { PowerPathType, StepKeyName } from '../types';

export const PP_SNG_TOURNAMENT_GROUP = 'PATHSNG2';

export const getPPDataTypeKey = (dataType: string): PPDataType =>
  dataType === 'mock' ? 'mock' : 'real';

export const generateStepKey = (step: number, type: PowerPathType): StepKey => {
  if (step === 1) {
    return StepKeyName.step1;
  } else if (step === 2) {
    if (type === PowerPathType.SIT_AND_GO) {
      return StepKeyName.step2SNG;
    }
    return StepKeyName.step2Scheduled;
  } else if (step === 3) {
    return StepKeyName.step3;
  } else if (step === 4) {
    return StepKeyName.step4;
  }
  throw new Error('Invalid step or type provided');
};

export const normalizePassesResponse = (
  passesResponse: PassBundleRaw,
  site: string,
  country: string
): PassBundleRaw => {
  if (!passesResponse) return { gold: [], silver: [], bronze: [] };

  const { gold, silver, bronze } = passesResponse;
  return {
    gold: filterPassesData(gold, site, country),
    silver: filterPassesData(silver, site, country),
    bronze: filterPassesData(bronze, site, country)
  };
};

export const filterTournaments = (
  filterVariables: FilterVariables,
  tournaments: Tournament[]
): Tournament[] =>
  filterMTTTournaments(filterVariables, tournaments).filter((tournament) => {
    if (tournament.pokerNgGroup === PP_SNG_TOURNAMENT_GROUP) {
      return ![TournamentState.RUNNING, TournamentState.COMPLETED].includes(
        tournament.formattedState
      );
    }

    return true;
  });

const TOURNAMENT_ORDER: Partial<{
  [K in TournamentState]: number;
}> & { default: number; sng: number } = {
  // sort all others at the bottom of the list
  default: 100,
  [TournamentState.CANCELLED]: 0,
  [TournamentState.COMPLETED]: 1,
  [TournamentState.PAUSED]: 2,
  [TournamentState.RUNNING]: 3,
  [TournamentState.LATE_REGISTRATION]: 4,
  [TournamentState.SEATING]: 5,
  sng: 6,
  [TournamentState.PHASE]: 7,
  [TournamentState.REGISTERING]: 8,
  [TournamentState.ANNOUNCED]: 9,
  [TournamentState.RESTRICTED]: 10
};

const getOrder = (tournament: Tournament): number => {
  if (tournament.pokerNgGroup === PP_SNG_TOURNAMENT_GROUP) {
    return TOURNAMENT_ORDER.sng;
  }

  return TOURNAMENT_ORDER[tournament.formattedState] || TOURNAMENT_ORDER.default;
};

export const rowStateTimeComparator = (
  rowTournamentA: Row<Tournament>,
  rowTournamentB: Row<Tournament>
) => stateTimeComparator(rowTournamentA.original, rowTournamentB.original, getOrder);

export const isPPTournamentSnG = (group: string) => group === PP_SNG_TOURNAMENT_GROUP;

const filterPassesData = (passes: Pass[] = [], site: string, country: string): Pass[] =>
  passes
    .filter((pass) => {
      const parsedCriteria = parseHSPCriteria(pass.targeting);
      return criteriaMatches(parsedCriteria, {
        site: [site?.toLowerCase() || ''],
        country: [country?.toLowerCase() || '']
      });
    })
    .sort((a, b) => a.order - b.order);
